import { NEVER, addYears, expiryCut } from './dates.js';
import { bookedBefore } from './rounding.js';
export const createLedger = (periods, pinned = false) => ({
    tranches: [],
    periods,
    pinned,
    closed: null,
    waiver: 0,
    activeAmount: 0,
    paid: 0,
    corrected: 0,
    writtenOff: 0,
    rolling: [],
    rollingHead: 0,
    groups: [],
    nextExpiry: NEVER,
    scanFrom: 0,
    capitalizedBefore: Number.NEGATIVE_INFINITY,
    lastExpired: [],
    paidThrough: Number.NEGATIVE_INFINITY,
    openByStream: [],
});
export const isActive = (tranche) => !tranche.pending;
const addOpen = (ledger, tranche, amount) => {
    if (isActive(tranche)) {
        ledger.openByStream[tranche.stream] =
            (ledger.openByStream[tranche.stream] ?? 0) + amount;
    }
};
export const trancheOpen = (tranche) => tranche.amount - tranche.paid - tranche.writtenOff;
export const isOpen = (tranche) => isActive(tranche) && trancheOpen(tranche) > 0;
// The rolling scan passes a tranche settled, or expiring with a group.
const leftRolling = (tranche) => trancheOpen(tranche) <= 0 || tranche.group !== null;
const noteExpiry = (ledger, tranche) => {
    if (!tranche.neverExpires) {
        ledger.nextExpiry = Math.min(ledger.nextExpiry, (tranche.group?.at ?? tranche.oldestDayExpiry) + 1);
    }
};
// What the tranche holds on its days before `day`.
export const amountBefore = (tranche, day) => {
    if (day <= tranche.from) {
        return 0;
    }
    if (day >= tranche.to) {
        return tranche.amount;
    }
    return bookedBefore(tranche, day) - bookedBefore(tranche, tranche.from);
};
// What is open of the tranche's days before `day`.
export const openBefore = (tranche, day) => Math.max(0, amountBefore(tranche, day) - tranche.paid);
export const amountOn = (tranche, day) => amountBefore(tranche, day + 1) - amountBefore(tranche, day);
// One-day pieces are whole: what they hold is set, not derived.
const makeWhole = (tranche) => {
    if (tranche.to - tranche.from === 1) {
        tranche.origin = 0;
        tranche.originDay = tranche.from;
        tranche.perDay = tranche.amount;
    }
};
// The first day not paid in full, or `to`.
export const firstUnpaidDay = (tranche) => {
    let low = tranche.from;
    let high = tranche.to;
    while (low < high) {
        const middle = (low + high) >> 1;
        if (amountBefore(tranche, middle + 1) <= tranche.paid) {
            low = middle + 1;
        }
        else {
            high = middle;
        }
    }
    return low;
};
// The day after the last one with anything paid.
const paidTo = (tranche) => tranche.paid === 0
    ? tranche.from
    : Math.min(tranche.to, firstUnpaidDay(tranche) + 1);
// The days of a tranche as runs of the same whole øre, leaving out days of
// none, each with its share of what was paid and written off.
// What of `size` after the tranche's first `before` was paid and written off:
// payments settle a tranche from its first day, write-offs after them.
export const prefixShares = (tranche, before, size) => {
    const paid = Math.min(size, Math.max(0, tranche.paid - before));
    const settled = Math.min(size, Math.max(0, tranche.paid + tranche.writtenOff - before));
    return { paid, writtenOff: settled - paid };
};
export const runsOf = (tranche) => {
    if (Number.isInteger(tranche.origin) && Number.isInteger(tranche.perDay)) {
        return [tranche];
    }
    const runs = [];
    let before = 0;
    const addRun = (from, to, perDay) => {
        const amount = perDay * (to - from);
        runs.push({
            ...tranche,
            from,
            to,
            origin: 0,
            originDay: from,
            perDay,
            amount,
            ...prefixShares(tranche, before, amount),
        });
        before += amount;
    };
    let runFrom = tranche.from;
    let runPerDay = 0;
    for (let day = tranche.from; day < tranche.to; day++) {
        const due = amountOn(tranche, day);
        if (due !== runPerDay) {
            if (runPerDay > 0) {
                addRun(runFrom, day, runPerDay);
            }
            runFrom = day;
            runPerDay = due;
        }
    }
    if (runPerDay > 0) {
        addRun(runFrom, tranche.to, runPerDay);
    }
    return runs;
};
export const ledgerOpen = (ledger) => ledger.activeAmount - ledger.paid - ledger.writtenOff;
const isLump = (tranche) => tranche.to - tranche.from === 1 && tranche.perDay === tranche.amount;
// The day the tranche (its newest day, if rolling) expires; NEVER if it can't.
export const expiryOf = (ledger, tranche) => {
    if (tranche.neverExpires) {
        return NEVER;
    }
    if (tranche.group !== null) {
        return tranche.group.at;
    }
    return addYears(isLump(tranche) ? tranche.clockFrom : tranche.to - 1, ledger.periods.ordinary);
};
// Inserts keeping `rolling` ordered by clockFrom.
const insertRolling = (ledger, tranche) => {
    const { rolling } = ledger;
    let low = ledger.rollingHead;
    let high = rolling.length;
    while (low < high) {
        const middle = (low + high) >> 1;
        if (rolling[middle].clockFrom <= tranche.clockFrom) {
            low = middle + 1;
        }
        else {
            high = middle;
        }
    }
    if (low === rolling.length) {
        rolling.push(tranche);
    }
    else {
        rolling.splice(low, 0, tranche);
    }
};
// Where `tranche` goes from `low` on: by day, then stream, after the
// tranches it ties with. Within a day ordinary interest is paid before
// reinstated interest.
const sortedIndex = (ledger, tranche, low = 0) => {
    const { tranches } = ledger;
    let high = tranches.length;
    while (low < high) {
        const middle = (low + high) >> 1;
        const other = tranches[middle];
        if (other.from < tranche.from ||
            (other.from === tranche.from && other.stream <= tranche.stream)) {
            low = middle + 1;
        }
        else {
            high = middle;
        }
    }
    return low;
};
const insertTranche = (ledger, index, tranche) => {
    if (index === ledger.tranches.length) {
        ledger.tranches.push(tranche);
    }
    else {
        ledger.tranches.splice(index, 0, tranche);
    }
    if (index < ledger.scanFrom) {
        ledger.scanFrom = isActive(tranche) ? index : ledger.scanFrom + 1;
    }
};
// A split-off piece shares its original's limitation state.
const adopt = (ledger, piece) => {
    if (piece.group !== null) {
        piece.group.tranches.push(piece);
    }
    else if (!piece.neverExpires) {
        insertRolling(ledger, piece);
    }
};
// Rolling tranches passed over are dropped once this many have built up and
// they are most of the array.
const COMPACT_ROLLING_AFTER = 256;
const refreshNextExpiry = (ledger) => {
    let next = NEVER;
    for (const group of ledger.groups) {
        next = Math.min(next, group.at + 1);
    }
    const { rolling } = ledger;
    while (ledger.rollingHead < rolling.length &&
        leftRolling(rolling[ledger.rollingHead])) {
        ledger.rollingHead++;
    }
    if (ledger.rollingHead > COMPACT_ROLLING_AFTER &&
        ledger.rollingHead * 2 > rolling.length) {
        ledger.rolling = rolling.slice(ledger.rollingHead);
        ledger.rollingHead = 0;
    }
    const first = ledger.rolling[ledger.rollingHead];
    if (first !== undefined) {
        next = Math.min(next, first.oldestDayExpiry + 1);
    }
    ledger.nextExpiry = next;
};
// Splits so that a piece starts exactly at `day`; returns its index.
const splitAtDay = (ledger, index, day) => {
    const tranche = ledger.tranches[index];
    if (day <= tranche.from) {
        return index;
    }
    if (day >= tranche.to) {
        return index + 1;
    }
    const leftAmount = amountBefore(tranche, day);
    const leftPaid = Math.min(tranche.paid, leftAmount);
    const leftWrittenOff = Math.min(tranche.writtenOff, leftAmount - leftPaid);
    const right = {
        ...tranche,
        from: day,
        clockFrom: day,
        oldestDayExpiry: addYears(day, ledger.periods.ordinary),
        amount: tranche.amount - leftAmount,
        paid: tranche.paid - leftPaid,
        writtenOff: tranche.writtenOff - leftWrittenOff,
    };
    tranche.to = day;
    tranche.amount = leftAmount;
    tranche.paid = leftPaid;
    tranche.writtenOff = leftWrittenOff;
    makeWhole(tranche);
    makeWhole(right);
    const rightIndex = sortedIndex(ledger, right, index + 1);
    insertTranche(ledger, rightIndex, right);
    adopt(ledger, right);
    return rightIndex;
};
// Splits a one-day tranche into two same-day pieces at `offset`; returns the
// index of the second.
const splitWithinDay = (ledger, index, offset) => {
    const tranche = ledger.tranches[index];
    const leftPaid = Math.min(tranche.paid, offset);
    const leftWrittenOff = Math.min(tranche.writtenOff, offset - leftPaid);
    const right = {
        ...tranche,
        perDay: tranche.amount - offset,
        amount: tranche.amount - offset,
        paid: tranche.paid - leftPaid,
        writtenOff: tranche.writtenOff - leftWrittenOff,
    };
    tranche.perDay = offset;
    tranche.amount = offset;
    tranche.paid = leftPaid;
    tranche.writtenOff = leftWrittenOff;
    insertTranche(ledger, index + 1, right);
    adopt(ledger, right);
    return index + 1;
};
export const accrue = (ledger, input) => {
    const expiry = input.expiry ?? 'rolling';
    const tranche = {
        from: input.from,
        to: input.to,
        origin: input.origin ?? 0,
        originDay: input.originDay ?? input.from,
        perDay: input.perDay,
        amount: 0,
        paid: 0,
        writtenOff: 0,
        reason: null,
        group: null,
        neverExpires: expiry === 'never',
        clockFrom: input.clockFrom ?? input.from,
        oldestDayExpiry: addYears(input.clockFrom ?? input.from, ledger.periods.ordinary),
        pending: input.pending ?? false,
        expiredAt: null,
        stream: input.stream ?? 0,
        row: input.row,
    };
    tranche.amount =
        bookedBefore(tranche, tranche.to) - bookedBefore(tranche, tranche.from);
    makeWhole(tranche);
    const { amount } = tranche;
    if (typeof expiry === 'number') {
        const group = {
            at: expiry,
            basis: 'ordinary',
            tranches: [tranche],
        };
        tranche.group = group;
        ledger.groups.push(group);
    }
    insertTranche(ledger, sortedIndex(ledger, tranche), tranche);
    if (expiry === 'rolling') {
        insertRolling(ledger, tranche);
    }
    if (isActive(tranche)) {
        ledger.activeAmount += amount;
        addOpen(ledger, tranche, amount);
        if (ledger.closed !== null) {
            writeOff(ledger, tranche, ledger.closed);
        }
    }
    if (trancheOpen(tranche) > 0) {
        noteExpiry(ledger, tranche);
    }
    return tranche;
};
const payTranche = (ledger, tranche, take) => {
    tranche.paid += take;
    ledger.paid += take;
    addOpen(ledger, tranche, -take);
    ledger.paidThrough = Math.max(ledger.paidThrough, paidTo(tranche));
};
// Pays the oldest open active amounts first, by day, then stream, each
// tranche up to what `payable` allows of it; returns what was paid.
export const consume = (ledger, amount, payable = trancheOpen) => {
    const { tranches } = ledger;
    while (ledger.scanFrom < tranches.length &&
        isActive(tranches[ledger.scanFrom]) &&
        trancheOpen(tranches[ledger.scanFrom]) <= 0) {
        ledger.scanFrom++;
    }
    let paid = 0;
    for (let i = ledger.scanFrom; i < tranches.length && paid < amount; i++) {
        const tranche = tranches[i];
        if (!isOpen(tranche)) {
            continue;
        }
        // Tranches of several streams can share days.
        if (i + 1 < tranches.length && tranches[i + 1].from < tranche.to) {
            return paid + payTogether(ledger, amount - paid, payable, i);
        }
        const take = Math.min(amount - paid, payable(tranche));
        if (take > 0) {
            payTranche(ledger, tranche, take);
            paid += take;
        }
    }
    return paid;
};
// `consume` from tranche `next` on, paying the tranches that share days
// together, day by day.
const payTogether = (ledger, amount, payable, next) => {
    const { tranches } = ledger;
    let paid = 0;
    let payees = [];
    // Takes the next tranche starting by `day` with anything payable.
    const admit = (day) => {
        while (next < tranches.length && tranches[next].from <= day) {
            const tranche = tranches[next++];
            if (!isOpen(tranche)) {
                continue;
            }
            const left = Math.min(trancheOpen(tranche), payable(tranche));
            if (left > 0) {
                const payee = { tranche, left };
                payees.push(payee);
                return payee;
            }
        }
        return null;
    };
    // Returns what was paid.
    const pay = (payee, take) => {
        if (take <= 0) {
            return 0;
        }
        payTranche(ledger, payee.tranche, take);
        payee.left -= take;
        return take;
    };
    // What `payee` may pay of its days before `day`.
    const owedBefore = (payee, day) => Math.min(payee.left, openBefore(payee.tranche, day));
    while (paid < amount) {
        if (payees.length === 0 && admit(Number.POSITIVE_INFINITY) === null) {
            break;
        }
        let day = Math.min(...payees.map((payee) => firstUnpaidDay(payee.tranche)));
        for (let payee = admit(day); payee !== null; payee = admit(day)) {
            day = Math.min(day, firstUnpaidDay(payee.tranche));
        }
        // The same tranches pay until one of them ends or another starts.
        const end = Math.min(tranches[next]?.from ?? Number.POSITIVE_INFINITY, ...payees.map((payee) => payee.tranche.to));
        const remaining = amount - paid;
        const owed = (before) => payees.reduce((sum, payee) => sum + owedBefore(payee, before), 0);
        if (owed(end) <= remaining) {
            for (const payee of payees) {
                paid += pay(payee, owedBefore(payee, end));
            }
        }
        else {
            // Days before `last` are paid in full, and `last` in stream order.
            let last = day;
            let high = end;
            while (high - last > 1) {
                const middle = (last + high) >> 1;
                if (owed(middle) <= remaining) {
                    last = middle;
                }
                else {
                    high = middle;
                }
            }
            for (const payee of payees) {
                paid += pay(payee, owedBefore(payee, last));
            }
            for (const payee of payees.toSorted((a, b) => a.tranche.stream - b.tranche.stream)) {
                paid += pay(payee, Math.min(amount - paid, owedBefore(payee, last + 1)));
            }
        }
        payees = payees.filter((payee) => payee.left > 0 && trancheOpen(payee.tranche) > 0);
    }
    return paid;
};
const writeOff = (ledger, tranche, reason) => {
    const open = trancheOpen(tranche);
    if (open <= 0) {
        return 0;
    }
    tranche.writtenOff += open;
    tranche.reason = reason;
    if (isActive(tranche)) {
        ledger.writtenOff += open;
        addOpen(ledger, tranche, -open);
        return open;
    }
    return 0;
};
// Writes off everything open now and everything accrued later (irrecoverable,
// or expiry of the debt it is accessory to). Returns the amount written off.
export const close = (ledger, reason) => {
    ledger.closed = reason;
    let total = 0;
    for (const tranche of ledger.tranches) {
        total += writeOff(ledger, tranche, reason);
    }
    ledger.rolling = [];
    ledger.rollingHead = 0;
    ledger.groups = [];
    ledger.nextExpiry = NEVER;
    return total;
};
// Indexes of the tranches of `stream` covering `day`. Tranches are ordered by
// start, and those of one stream do not overlap.
export const covering = (ledger, day, stream) => {
    const { tranches } = ledger;
    let low = 0;
    let high = tranches.length;
    while (low < high) {
        const middle = (low + high) >> 1;
        if (tranches[middle].from <= day) {
            low = middle + 1;
        }
        else {
            high = middle;
        }
    }
    const found = [];
    for (let index = low - 1; index >= 0; index--) {
        const tranche = tranches[index];
        if (tranche.stream !== stream) {
            continue;
        }
        if (tranche.to <= day) {
            break;
        }
        found.push(index);
    }
    return found.toReversed();
};
// What of the tranche's share of `day` is paid; the paid part is a prefix.
export const paidOnDay = (tranche, day) => Math.min(amountOn(tranche, day), Math.max(0, tranche.paid - amountBefore(tranche, day)));
// What `stream` accrued on `day`, and what of it is paid.
export const dayAmounts = (ledger, day, stream) => {
    let amount = 0;
    let paid = 0;
    for (const index of covering(ledger, day, stream)) {
        const tranche = ledger.tranches[index];
        amount += amountOn(tranche, day);
        paid += paidOnDay(tranche, day);
    }
    return { amount, paid };
};
const expireTranche = (ledger, tranche, at) => {
    if (trancheOpen(tranche) > 0) {
        tranche.expiredAt = at;
        ledger.lastExpired.push(tranche);
    }
    return writeOff(ledger, tranche, 'expired');
};
// Writes off up to `amount` of the open interest of `stream` on `day` (from
// the end of the day's amount, so a paid prefix stays paid), into the day's
// one tranche of such write-offs. Reinstated interest is written off while
// still pending too. Returns what was written off.
export const writeOffDay = (ledger, day, stream, amount) => {
    const index = covering(ledger, day, stream).find((candidate) => trancheOpen(ledger.tranches[candidate]) > 0);
    if (index === undefined || amount <= 0) {
        return 0;
    }
    const start = splitAtDay(ledger, index, day);
    splitAtDay(ledger, start, day + 1);
    const open = ledger.tranches[start];
    const written = Math.min(amount, trancheOpen(open));
    const earlier = ledger.tranches.at(start + 1);
    if (earlier !== undefined &&
        earlier.from === day &&
        earlier.to === day + 1 &&
        earlier.stream === stream &&
        earlier.reason === 'expired' &&
        earlier.expiredAt === null &&
        earlier.paid === 0 &&
        trancheOpen(earlier) === 0) {
        // Moves the øre from the open tranche into the day's written-off one.
        open.amount -= written;
        open.perDay = open.amount;
        addOpen(ledger, open, -written);
        earlier.amount += written;
        earlier.perDay = earlier.amount;
        earlier.writtenOff += written;
        if (isActive(earlier)) {
            ledger.writtenOff += written;
        }
        return written;
    }
    const piece = written < open.amount
        ? ledger.tranches[splitWithinDay(ledger, start, open.amount - written)]
        : open;
    writeOff(ledger, piece, 'expired');
    return written;
};
// Gives back to the debt up to `amount` of what `writeOffDay` wrote off of
// `stream` on `day`: to the day's open piece, or, if all of the day is paid or
// written off, to a new piece with its own limitation state. Returns what was
// given back.
export const unwriteDay = (ledger, day, stream, amount) => {
    const pieces = covering(ledger, day, stream).map((index) => ledger.tranches[index]);
    const written = pieces.find((tranche) => tranche.reason === 'expired' &&
        tranche.expiredAt === null &&
        tranche.paid === 0 &&
        tranche.writtenOff > 0);
    if (written === undefined || amount <= 0) {
        return 0;
    }
    const back = Math.min(amount, written.writtenOff);
    const open = pieces.find((tranche) => tranche.reason === null &&
        tranche.to - tranche.from === 1 &&
        trancheOpen(tranche) > 0);
    if (open === undefined) {
        const reopened = ledger.tranches[splitWithinDay(ledger, ledger.tranches.indexOf(written), written.amount - back)];
        reopened.writtenOff = 0;
        reopened.reason = null;
        addOpen(ledger, reopened, back);
        noteExpiry(ledger, reopened);
    }
    else {
        written.amount -= back;
        written.perDay = written.amount;
        written.writtenOff -= back;
        open.amount += back;
        open.perDay = open.amount;
        addOpen(ledger, open, back);
    }
    if (isActive(written)) {
        ledger.writtenOff -= back;
    }
    return back;
};
// Writes off every open part whose limitation passed before `at`, returning
// the active amount written off.
export const expire = (ledger, at) => {
    ledger.lastExpired = [];
    if (at < ledger.nextExpiry) {
        return 0;
    }
    let total = 0;
    ledger.groups = ledger.groups.filter((group) => {
        if (group.at >= at) {
            return true;
        }
        for (const tranche of group.tranches) {
            total += expireTranche(ledger, tranche, at);
        }
        return false;
    });
    const { rolling } = ledger;
    let cut = null;
    while (ledger.rollingHead < rolling.length) {
        const tranche = rolling[ledger.rollingHead];
        if (leftRolling(tranche)) {
            ledger.rollingHead++;
            continue;
        }
        if (isLump(tranche)) {
            if (tranche.oldestDayExpiry >= at) {
                break;
            }
            total += expireTranche(ledger, tranche, at);
            ledger.rollingHead++;
            continue;
        }
        cut ??= expiryCut(at, ledger.periods.ordinary);
        if (cut <= tranche.from) {
            break;
        }
        if (cut < tranche.to) {
            // The newer part stays rolling; it sorts right after this one.
            splitAtDay(ledger, ledger.tranches.indexOf(tranche), cut);
        }
        total += expireTranche(ledger, tranche, at);
        ledger.rollingHead++;
    }
    refreshNextExpiry(ledger);
    return total;
};
// Debt whose limitation passed without it being marked expired stays owed, as
// in v1, and nothing the debtor does extends it any more (D15).
export const bar = (ledger) => {
    ledger.nextExpiry = NEVER;
};
// An interruption at `at`: every open part that existed before it gets a new
// period counted from `at`. A foundation also moves those parts to the
// foundation basis. Pending reinstated interest is not yet debt, so nothing
// before its reapplication extends it (D8).
export const extend = (ledger, at, basis) => {
    // Nothing open can expire, so there is nothing to extend.
    if (ledger.pinned || ledger.nextExpiry === NEVER) {
        return;
    }
    extendOpen(ledger, at, basis);
};
// A court's decision at `at` renews barred debt as well, by the foundation
// period (D2).
export const renew = (ledger, at) => {
    if (ledger.pinned || ledgerOpen(ledger) <= 0) {
        return;
    }
    extendOpen(ledger, at, 'foundation');
};
const extendOpen = (ledger, at, basis) => {
    const extended = {
        ordinary: addYears(at, ledger.periods.ordinary),
        foundation: addYears(at, ledger.periods.foundation),
    };
    for (const group of ledger.groups) {
        if (basis === 'foundation') {
            group.basis = 'foundation';
        }
        group.at = Math.max(group.at, extended[group.basis]);
    }
    // Rolling tranches that existed before `at` join a group from `at`.
    const covered = [];
    const pending = [];
    const { rolling } = ledger;
    while (ledger.rollingHead < rolling.length) {
        const tranche = rolling[ledger.rollingHead];
        if (leftRolling(tranche)) {
            ledger.rollingHead++;
            continue;
        }
        if (tranche.pending) {
            pending.push(tranche);
            ledger.rollingHead++;
            continue;
        }
        const lump = isLump(tranche);
        if (lump ? tranche.from > at : tranche.from >= at) {
            break;
        }
        if (lump && tranche.oldestDayExpiry > extended[basis]) {
            break;
        }
        if (!lump && tranche.to > at) {
            splitAtDay(ledger, ledger.tranches.indexOf(tranche), at);
        }
        covered.push(tranche);
        ledger.rollingHead++;
    }
    // Pending tranches stay rolling, back in the consumed slots before the head:
    // they are ordered and no later than what remains.
    for (let i = pending.length - 1; i >= 0; i--) {
        rolling[--ledger.rollingHead] = pending[i];
    }
    if (covered.length > 0) {
        const group = { at: extended[basis], basis, tranches: covered };
        for (const tranche of covered) {
            tranche.group = group;
        }
        ledger.groups.push(group);
    }
    // Groups that now share an expiry become one, moving the smaller group.
    const { groups } = ledger;
    for (let i = 0; i < groups.length; i++) {
        for (let j = groups.length - 1; j > i; j--) {
            if (groups[j].at !== groups[i].at ||
                groups[j].basis !== groups[i].basis) {
                continue;
            }
            const [larger, smaller] = groups[i].tranches.length >= groups[j].tranches.length
                ? [groups[i], groups[j]]
                : [groups[j], groups[i]];
            for (const tranche of smaller.tranches) {
                tranche.group = larger;
                larger.tranches.push(tranche);
            }
            groups[i] = larger;
            groups.splice(j, 1);
        }
    }
    refreshNextExpiry(ledger);
};
// Makes pending tranches part of the debt; parts that already expired never
// become due.
export const activate = (ledger, matches) => {
    const activated = [];
    for (const tranche of ledger.tranches) {
        if (isActive(tranche) || !matches(tranche)) {
            continue;
        }
        tranche.pending = false;
        if (tranche.writtenOff > 0) {
            tranche.amount = 0;
            tranche.origin = 0;
            tranche.originDay = tranche.from;
            tranche.perDay = 0;
            tranche.writtenOff = 0;
            tranche.reason = null;
        }
        ledger.activeAmount += tranche.amount;
        addOpen(ledger, tranche, trancheOpen(tranche));
        activated.push(tranche);
    }
    ledger.scanFrom = 0;
    return activated;
};
// Corrections of interest consume the oldest accrual as it accrues.
export const applyWaiver = (ledger) => {
    if (ledger.waiver > 0) {
        const corrected = consume(ledger, ledger.waiver);
        ledger.waiver -= corrected;
        ledger.corrected += corrected;
    }
};
//# sourceMappingURL=ledger.js.map